/**
 * Which panel the task pane opens on.
 *
 * The four ribbon buttons (Kuis, Interaksi, Narasi, Terbitkan) are `ShowTaskpane`
 * actions that share one pane container. Office keeps that container open and
 * replaces its contents with the clicked action's `SourceLocation`, so the only
 * thing distinguishing one button from another is the URL — each carries
 * `?tab=<id>`. Without reading it, every button lands on whatever panel was
 * already showing, which is exactly how the ribbon appeared dead.
 *
 * The remembered tab is the fallback for a reload nobody asked for: PowerPoint
 * rebuilds the pane iframe freely, and coming back on a different panel than the
 * author left reads as lost work.
 */
export const TAB_IDS = ['deck', 'quiz', 'interaction', 'narration', 'publish'] as const;

export type Tab = (typeof TAB_IDS)[number];

const TAB_KEY = 'slideforge.ui.tab';

export function isTab(value: unknown): value is Tab {
  return typeof value === 'string' && (TAB_IDS as readonly string[]).includes(value);
}

/** Reads the ribbon's choice, then the remembered one, then the deck panel. */
export function initialTab(): Tab {
  const requested = requestedTab();
  if (requested) return requested;
  try {
    const remembered = globalThis.localStorage?.getItem(TAB_KEY);
    if (isTab(remembered)) return remembered;
  } catch {
    // Private mode and locked-down hosts throw on storage access.
  }
  return 'deck';
}

/**
 * The tab named by the URL.
 *
 * Both `?tab=` and `#tab=` are honoured. Office is documented to open the
 * action's `SourceLocation`, which is a query string here, but a host that
 * normalises the URL into a fragment must not silently land on the wrong panel.
 */
function requestedTab(): Tab | undefined {
  try {
    const url = new URL(String(globalThis.location?.href ?? ''));
    const fromQuery = url.searchParams.get('tab');
    if (isTab(fromQuery)) return fromQuery;
    const hash = url.hash.replace(/^#/, '');
    const fromHash = hash.startsWith('tab=') ? hash.slice(4) : hash;
    if (isTab(fromHash)) return fromHash;
  } catch {
    // Not a URL worth parsing — fall through to the remembered tab.
  }
  return undefined;
}

/**
 * Records the current tab so a pane reload returns to it.
 *
 * The URL is rewritten too: a reload triggered by Office replays the address
 * bar, and leaving a stale `?tab=` there would drag the author back to the panel
 * they navigated away from.
 */
export function rememberTab(tab: Tab): void {
  try {
    globalThis.localStorage?.setItem(TAB_KEY, tab);
  } catch {
    // Storage is a convenience here, never a requirement.
  }
  try {
    const url = new URL(String(globalThis.location?.href ?? ''));
    if (url.searchParams.get('tab') === tab) return;
    url.searchParams.set('tab', tab);
    globalThis.history?.replaceState?.(null, '', url.toString());
  } catch {
    // A host that forbids history rewriting still has the stored value.
  }
}
